<template>
  <div class="restricao-container">
    <!-- Add Restriction Form -->
    <div class="restricao-form">
      <div class="restricao-form__row">
        <div class="restricao-form__field restricao-form__field--tipo">
          <label>Tipo</label>
          <select
            v-model="restricao.tipo"
          >
            <option value="">Selecione...</option>
            <option
              v-for="tipo in tiposRestricao"
              :key="tipo.chave"
              :value="tipo.chave"
            >
              {{ tipo.label }}
            </option>
          </select>
        </div>

        <div class="restricao-form__field restricao-form__field--chave">
          <label>{{ chaveLabel }}</label>
          <input
            type="text"
            v-model="restricao.chave"
            maxlength="250"
            placeholder="Digite o código..."
            :disabled="!restricao.tipo"
            @keyup.enter="addRestricao"
          />
        </div>

        <div class="restricao-form__action">
          <button
            class="restricao-form__add-btn"
            :disabled="!canAdd"
            @click="addRestricao"
            title="Adicionar restrição"
          >
            <CIcon name="cil-plus" size="sm" />
            Adicionar
          </button>
        </div>
      </div>
    </div>

    <!-- Selected Restrictions -->
    <div class="restricao-selected">
      <div class="restricao-selected__label">
        Restrições ativas ({{ item.restricoes.length }})
      </div>

      <div v-if="item.restricoes.length > 0" class="restricao-list">
        <div
          v-for="(restricaoItem, index) in item.restricoes"
          :key="index"
          class="restricao-row"
        >
          <span class="restricao-row__badge" :style="badgeStyle(restricaoItem.tipo)">
            {{ badgeLabel(restricaoItem.tipo) }}
          </span>
          <div class="restricao-row__info">
            <span class="restricao-row__tipo">{{ tipoLabel(restricaoItem.tipo) }}</span>
            <span class="restricao-row__sep">&middot;</span>
            <span class="restricao-row__chave">{{ restricaoItem.chave }}</span>
          </div>
          <button
            class="restricao-row__remove"
            @click="removeRestricao(index)"
            title="Remover restrição"
          >
            <CIcon name="cil-x-circle" size="sm" />
          </button>
        </div>
      </div>

      <div v-else class="restricao-empty">
        <CIcon name="cil-ban" size="xl" class="restricao-empty__icon" />
        <p>Nenhuma restrição adicionada</p>
        <p class="restricao-empty__hint">Restrições limitam onde a sinaleira será aplicada</p>
      </div>
    </div>
  </div>
</template>

<script>
import _find from "lodash/find";

const CORES_TIPOS = [
  "#3a3e81", "#2298a3", "#e67e22", "#8e44ad", "#27ae60",
  "#2980b9", "#c0392b", "#d35400", "#16a085", "#7f8c8d", "#e74c3c",
];

export default {
  name: "StepRestricoes",
  props: {
    item: { type: Object, required: true },
    tiposRestricao: { type: Array, default: () => [] },
  },
  data() {
    return {
      restricao: {
        tipo: "",
        chave: "",
      },
    };
  },
  computed: {
    tiposMap() {
      const map = {};
      (this.tiposRestricao || []).forEach((tipo, i) => {
        map[tipo.chave] = {
          label: tipo.label,
          short: tipo.chave.substring(0, 3).toUpperCase(),
          color: CORES_TIPOS[i % CORES_TIPOS.length],
        };
      });
      return map;
    },
    chaveLabel() {
      if (!this.restricao.tipo) return "Código";
      const config = this.tiposMap[this.restricao.tipo];
      return config ? `Código ${config.label.toLowerCase()}` : "Código";
    },
    canAdd() {
      if (!this.restricao.tipo || !this.restricao.chave) return false;
      return !this.isDuplicate();
    },
  },
  watch: {
    "restricao.tipo"() {
      this.restricao.chave = "";
    },
  },
  methods: {
    addRestricao() {
      if (!this.canAdd) return;
      this.item.restricoes.push({ ...this.restricao });
      this.restricao.chave = "";
    },
    removeRestricao(i) {
      this.item.restricoes.splice(i, 1);
    },
    isDuplicate() {
      return !!_find(this.item.restricoes, {
        tipo: this.restricao.tipo,
        chave: this.restricao.chave,
      });
    },
    tipoLabel(tipo) {
      return this.tiposMap[tipo]?.label || tipo;
    },
    badgeLabel(tipo) {
      return this.tiposMap[tipo]?.short || tipo;
    },
    badgeStyle(tipo) {
      const color = this.tiposMap[tipo]?.color || "#888";
      return {
        backgroundColor: color + "14",
        color: color,
        borderColor: color + "30",
      };
    },
  },
};
</script>

<style scoped>
/* ===== LAYOUT ===== */
.restricao-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* ===== ADD FORM ===== */
.restricao-form {
  border: 1px solid #e4e7eb;
  border-radius: 10px;
  padding: 16px;
  background: #f9fafb;
}

.restricao-form__row {
  display: flex;
  align-items: flex-end;
  gap: 12px;
}

.restricao-form__field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.restricao-form__field--tipo {
  flex: 0 0 220px;
}

.restricao-form__field--chave {
  flex: 1;
}

.restricao-form__field label {
  font-size: 11px;
  font-weight: 600;
  color: #667085;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.restricao-form__field select,
.restricao-form__field input {
  font-size: 13px;
  border-radius: 6px;
  border: 1px solid #dddce9;
  padding: 8px 10px;
  background-color: #fff;
  transition: border-color 0.15s ease;
  width: 100%;
}

.restricao-form__field select:focus,
.restricao-form__field input:focus {
  border-color: #3a3e81;
  outline: none;
  box-shadow: 0 0 0 2px rgba(58, 62, 129, 0.1);
}

.restricao-form__field select:disabled,
.restricao-form__field input:disabled {
  background-color: #f2f4f7;
  cursor: not-allowed;
}

.restricao-form__action {
  flex-shrink: 0;
}

.restricao-form__add-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: 1px solid #3a3e81;
  border-radius: 6px;
  background: #3a3e81;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}

.restricao-form__add-btn:hover:not(:disabled) {
  background: #2e3270;
}

.restricao-form__add-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* ===== SELECTED LIST ===== */
.restricao-selected__label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: #888;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
}

.restricao-list {
  border: 1px solid #e4e7eb;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
  max-height: 350px;
  overflow-y: auto;
}

.restricao-row {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  gap: 10px;
  border-bottom: 1px solid #f2f4f7;
  transition: background 0.1s;
}

.restricao-row:last-child {
  border-bottom: none;
}

.restricao-row:hover {
  background: #f9fafb;
}

.restricao-row__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 3px 8px;
  border-radius: 4px;
  border: 1px solid;
  flex-shrink: 0;
  min-width: 36px;
}

.restricao-row__info {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  min-width: 0;
}

.restricao-row__tipo {
  color: #667085;
  font-weight: 500;
  white-space: nowrap;
}

.restricao-row__sep {
  color: #d0d5dd;
}

.restricao-row__chave {
  color: #1d2939;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.restricao-row__remove {
  background: none;
  border: 1px solid transparent;
  border-radius: 6px;
  padding: 6px;
  cursor: pointer;
  color: #98a2b3;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  flex-shrink: 0;
}

.restricao-row__remove:hover {
  border-color: #e74c3c;
  color: #e74c3c;
  background: #fef2f2;
}

/* ===== EMPTY STATE ===== */
.restricao-empty {
  text-align: center;
  padding: 32px 20px;
  color: #98a2b3;
  border: 1px dashed #e4e7eb;
  border-radius: 10px;
}

.restricao-empty__icon {
  color: #d0d5dd;
  margin-bottom: 8px;
}

.restricao-empty p {
  font-size: 13px;
  margin: 0;
}

.restricao-empty__hint {
  font-size: 12px;
  color: #b0b8c4;
  margin-top: 4px;
}

/* ===== RESPONSIVE ===== */
@media (max-width: 768px) {
  .restricao-form__row {
    flex-direction: column;
    align-items: stretch;
  }

  .restricao-form__field--tipo {
    flex: 1;
  }

  .restricao-row__info {
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
  }

  .restricao-row__sep {
    display: none;
  }
}
</style>
